import { Input } from "@/core/components/ui/input";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { Typography } from "@/core/components/ui/typography";
import {
  AP_ACTION_TYPE,
  type ApAgentAction,
  type ApPoLineItem,
} from "@/accounts-payable/types";
import { formatDollars, humanizePoField } from "@/accounts-payable/utils";
import { LineItemsEditor } from "./line-items-editor";
import { CostChangePct, SectionLabel } from "./shared";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = Record<string, any>;

/** The free-text reason, full width like every other field. */
const ReasonField = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) => (
  <Stack gap="xs" fullWidth>
    <SectionLabel>Reason</SectionLabel>
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Reason"
      rows={3}
    />
  </Stack>
);

/** Editable form for a pending action's proposed payload. */
export const EditablePayload = ({
  action,
  draft,
  setDraft,
}: {
  action: ApAgentAction;
  draft: Draft;
  setDraft: (next: Draft) => void;
}) => {
  const set = (key: string, value: unknown) =>
    setDraft({ ...draft, [key]: value });
  const setItem = (index: number, key: string, value: unknown) => {
    const items = (draft.items as ApPoLineItem[]).map((it, i) =>
      i === index ? { ...it, [key]: value } : it,
    );
    setDraft({ ...draft, items });
  };
  const setChange = (key: string, value: unknown) =>
    set("changes", { ...draft.changes, [key]: value });
  const setChangeItem = (index: number, key: string, value: unknown) => {
    const items = (draft.changes.items as ApPoLineItem[]).map((it, i) =>
      i === index ? { ...it, [key]: value } : it,
    );
    setChange("items", items);
  };

  switch (action.action_type) {
    case AP_ACTION_TYPE.matchJob:
      return (
        <Input
          label="Job #"
          type="text"
          inputMode="numeric"
          value={draft.st_job_external_id}
          onChange={(e) => set("st_job_external_id", e.target.value)}
          fullWidth
        />
      );
    case AP_ACTION_TYPE.linkVendor:
      return (
        <Stack gap="md" fullWidth>
          <Input
            label="Vendor #"
            type="text"
            inputMode="numeric"
            value={draft.st_vendor_id}
            onChange={(e) => set("st_vendor_id", e.target.value)}
            fullWidth
          />
          <Input
            label="Vendor name"
            value={draft.st_vendor_name}
            onChange={(e) => set("st_vendor_name", e.target.value)}
            fullWidth
          />
        </Stack>
      );
    case AP_ACTION_TYPE.updatePricebookCost: {
      const current = Number(draft.current_cost) || 0;
      const proposed = Number(draft.proposed_cost) || 0;
      return (
        <Stack gap="md" fullWidth>
          <Stack direction="row" gap="xl" fullWidth className="flex-wrap">
            <Stack gap="xs">
              <SectionLabel>SKU</SectionLabel>
              <Typography variant="bodyW2">{draft.sku}</Typography>
            </Stack>
            <Stack gap="xs">
              <SectionLabel>Current cost</SectionLabel>
              <Typography variant="bodyW2" className="tabular-nums">
                {formatDollars(draft.current_cost)}
              </Typography>
            </Stack>
          </Stack>
          <Input
            label="Recommended cost ($)"
            type="text"
            inputMode="decimal"
            value={draft.proposed_cost}
            onChange={(e) => set("proposed_cost", e.target.value)}
            fullWidth
          />
          <CostChangePct
            current={current}
            proposed={proposed}
            suffix="vs current"
            className="text-md"
          />
        </Stack>
      );
    }
    case AP_ACTION_TYPE.createPricebookItem:
      // Same order and labels as the view. Type is shown, not edited: the
      // item's type is decided by the pricebook section it's created in.
      return (
        <Stack gap="md" fullWidth>
          <Input
            label="Name"
            value={draft.name}
            onChange={(e) => set("name", e.target.value)}
            fullWidth
          />
          <Input
            label="Description"
            value={draft.description}
            onChange={(e) => set("description", e.target.value)}
            fullWidth
          />
          <Input
            label="Code"
            value={draft.code}
            onChange={(e) => set("code", e.target.value)}
            fullWidth
          />
          <Stack direction="row" gap="md" align="end" fullWidth>
            {draft.item_type && (
              <Stack gap="xs" className="min-w-0 flex-1">
                <SectionLabel>Type</SectionLabel>
                <Typography variant="bodyW2">
                  {draft.item_type.charAt(0).toUpperCase() +
                    draft.item_type.slice(1)}
                </Typography>
              </Stack>
            )}
            <Input
              label="Cost ($)"
              type="text"
              inputMode="decimal"
              value={draft.cost}
              onChange={(e) => set("cost", e.target.value)}
              fullWidth
            />
          </Stack>
        </Stack>
      );
    case AP_ACTION_TYPE.updatePo: {
      // Same order as the view: the scalar fields, then the line items. The
      // `items` change is a line-item array, edited through its sub-fields
      // rather than bound to a single text input.
      const changes = Object.entries(
        draft.changes as Record<string, string | ApPoLineItem[]>,
      );
      const items = changes.find(([, v]) => Array.isArray(v));
      return (
        <Stack gap="md" fullWidth>
          {changes
            .filter(([, v]) => !Array.isArray(v))
            .map(([k, v]) => (
              <Input
                key={k}
                label={humanizePoField(k)}
                value={v as string}
                onChange={(e) => setChange(k, e.target.value)}
                fullWidth
              />
            ))}
          {items && (
            <LineItemsEditor
              items={items[1] as ApPoLineItem[]}
              onChange={setChangeItem}
            />
          )}
          <ReasonField
            value={draft.reason ?? ""}
            onChange={(next) => set("reason", next)}
          />
        </Stack>
      );
    }
    case AP_ACTION_TYPE.createPo:
    case AP_ACTION_TYPE.createReturn:
      return (
        <Stack gap="md" fullWidth>
          <ReasonField
            value={draft.reason ?? ""}
            onChange={(next) => set("reason", next)}
          />
          <LineItemsEditor
            items={draft.items as ApPoLineItem[]}
            onChange={setItem}
          />
        </Stack>
      );
    case AP_ACTION_TYPE.markNonJob:
    case AP_ACTION_TYPE.flagForReview:
    case AP_ACTION_TYPE.noAction:
      return (
        <ReasonField
          value={draft.reason ?? ""}
          onChange={(next) => set("reason", next)}
        />
      );
    // `merge_vendor` deliberately has no form: see `hasEditablePayload`.
    default:
      return null;
  }
};
